import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: 'Message Sent!',
          description: response.data.message,
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send message. Please try again or contact us directly.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin size={24} />,
      title: 'Visit Us',
      details: [
        '11th Cross, Coffee Board Layout',
        'Mariyannapalya, St.Anthony Badavane',
        'Chowramma Layout, H.A.Farm Post',
        'Bangalore: 560024'
      ],
      link: null
    },
    {
      icon: <Phone size={24} />,
      title: 'Call Us',
      details: ['+91 99021 99065', '+91 95603 10830'],
      link: null
    },
    {
      icon: <Mail size={24} />,
      title: 'Email Us',
      details: ['classic.decor24@gmail.com', 'vijay.classicdecor@gmail.com'],
      link: null
    }
  ];

  return (
    <section id="contact" className="py-24 px-6 bg-[#0a0a0c]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-20">
          <p className="text-[#FF8C42] text-sm tracking-[0.3em] uppercase mb-4">Get In Touch</p>
          <h2
            className="text-4xl md:text-5xl font-serif text-white mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Let's Create Together
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Have a project in mind? We'd love to hear from you. Get in touch and let's discuss how we can bring your vision to life.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-[#1a1a1c] p-10 rounded-sm">
            <h3 className="text-2xl font-serif text-white mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              Send us a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-white/80 mb-2 text-sm">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-sm text-white focus:border-[#FF8C42] focus:outline-none focus:ring-1 focus:ring-[#FF8C42] transition-colors"
                  placeholder="Rahul Kumar"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-white/80 mb-2 text-sm">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-sm text-white focus:border-[#FF8C42] focus:outline-none focus:ring-1 focus:ring-[#FF8C42] transition-colors"
                  placeholder="rahul.kumar@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-white/80 mb-2 text-sm">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-sm text-white focus:border-[#FF8C42] focus:outline-none focus:ring-1 focus:ring-[#FF8C42] transition-colors"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-white/80 mb-2 text-sm">
                  Project Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-sm text-white focus:border-[#FF8C42] focus:outline-none focus:ring-1 focus:ring-[#FF8C42] transition-colors resize-none"
                  placeholder="Tell us about your project..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#FF8C42] hover:bg-[#ff9d5c] text-white px-8 py-4 rounded-sm font-medium tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-[#FF8C42]/30 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                <Send size={18} />
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            {contactInfo.map((info, index) => (
              <div key={index} className="bg-[#1a1a1c] p-8 rounded-sm hover:shadow-lg hover:shadow-[#FF8C42]/10 transition-all duration-300">
                <div className="flex items-start gap-6">
                  <div className="text-[#FF8C42] flex-shrink-0">{info.icon}</div>
                  <div>
                    <h4 className="text-white text-lg font-medium mb-3">{info.title}</h4>
                    {info.link ? (
                      <a
                        href={info.link}
                        className="text-white/70 hover:text-[#FF8C42] transition-colors"
                      >
                        {info.details[0]}
                      </a>
                    ) : (
                      <div className="text-white/70 space-y-1">
                        {info.details.map((detail, idx) => (
                          info.title === 'Call Us' ? (
                            <a 
                              key={idx}
                              href={`tel:${detail.replace(/\s/g, '')}`}
                              className="block hover:text-[#FF8C42] transition-colors"
                            >
                              {detail}
                            </a>
                          ) : info.title === 'Email Us' ? (
                            <a 
                              key={idx}
                              href={`mailto:${detail}`}
                              className="block hover:text-[#FF8C42] transition-colors"
                            >
                              {detail}
                            </a>
                          ) : (
                            <p key={idx}>{detail}</p>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Business Hours */}
            <div className="bg-[#1a1a1c] p-8 rounded-sm">
              <h4 className="text-white text-lg font-medium mb-4">Business Hours</h4>
              <div className="space-y-2 text-white/70">
                <div className="flex justify-between">
                  <span>Monday - Friday</span>
                  <span>9:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday</span>
                  <span>10:00 AM - 4:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday</span>
                  <span>Closed</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;